import styled from "@emotion/styled";
import { UseQueryResult } from "@tanstack/react-query";
import React from "react";
import { flushSync } from "react-dom";
import { Page } from "../../../activities/AppPage";
import { HRApplicationForm, HRSection } from "../../../messages";
import { STRINGS } from "../../../strings";
import { translate } from "../../../translate";
import { arrayAt } from "../../../utils/arrayUtils";
import { assertDefined } from "../../../utils/assertUndefinedable";
import {
  getSectionTitle,
  isSectionCompleted,
  isSectionInProgress,
} from "../../Onboarding/utils/onboardingUtils";
import useModal from "../../useModal";
import { default as SetupWizardVector } from "/public/assets/setup-wizard.svg?react";

interface Props {
  section: HRSection;
  sections: HRSection[];
  language: string;
  caregiverApplicationQuery: UseQueryResult<HRApplicationForm, unknown>;
  hasCompletedOrientation: boolean;
  onClickGoBack: () => void;
  onRequestNavigateReplace: (page: Page) => void;
  onRequestStartWatchingOrientation: () => void;
  onClickGoToFirstUnansweredStage: () => void;
}

const SetupWizardVectorWrapper = styled.div`
  svg {
    width: 200px;
    height: 200px;
  }
`;

function useNextStageModal(props: Props) {
  const [isOpen, setOpen] = React.useState(false);
  const [sections, setSections] = React.useState<HRSection[]>(props.sections);
  const [title, setTitle] = React.useState("");
  const [message, setMessage] = React.useState(<></>);
  const modal = useModal();

  const getNextSection = (newSections?: HRSection[]) => {
    return arrayAt(
      (newSections ?? sections)
        .filter(
          (s) => s.data.id !== props.section.data.id && !isSectionCompleted(s) && !s.data.locked
        )
        .sort((a, b) => sortBooleansTrueLast(isSectionInProgress(a), isSectionInProgress(b))),
      0
    );
  };

  const handleSectionFinished = (newSections?: HRSection[]) => {
    const currentSections = newSections ?? props.sections;

    if (newSections !== undefined) {
      flushSync(() => setSections(newSections));
    }

    const nextSection = getNextSection(newSections);
    const currentSection = assertDefined(
      currentSections.find((s) => s.data.id === props.section.data.id)
    );
    const hasNextSection = nextSection !== undefined;

    if (isSectionCompleted(currentSection)) {
      return handleCompletedSectionFinished(hasNextSection);
    }

    handleUncompletedSectionFinished(hasNextSection);
  };

  const handleCompletedSectionFinished = (hasNextSection: boolean) => {
    if (!hasNextSection) {
      return props.hasCompletedOrientation
        ? modal.setModalAsSuccess({
            title: translate(STRINGS.ONBOARDING_COMPLETED),
            message: translate(STRINGS.YOURE_ALL_DONE_LETS_GET_YOU_WORKING),
            closeText: translate(STRINGS.START_WORKING),
            onRequestClose: () => props.onRequestNavigateReplace({ type: "Main" }),
          })
        : modal.setModalAsSuccess({
            title: translate(STRINGS.ONBOARDING_COMPLETED),
            message: translate(STRINGS.ONBOARDING_COMPLETED_MESSAGE),
            closeText: translate(STRINGS.CONTINUE_TO_ORIENTATION),
            actions: [
              {
                key: "orientation",
                type: "button",
                children: translate(STRINGS.CONTINUE_TO_ORIENTATION),
                variant: "soft",
                color: "success",
                onClick: () => {
                  modal.setModalAsClosed();
                  props.onRequestStartWatchingOrientation();
                },
              },
              {
                key: "close",
                type: "button",
                children: translate(STRINGS.BACK_TO_ONBOARDING),
                variant: "link",
                onClick: () => {
                  modal.setModalAsClosed();
                  props.onClickGoBack();
                },
              },
            ],
          });
    }

    setTitle(translate(STRINGS.CONGRATULATIONS));
    setMessage(
      <p>
        {translate(STRINGS.YOU_HAVE_SUCCESSFULLY_COMPLETED_SECTION).replace(
          "%section%",
          getSectionTitle(props.section, props.language)
        )}
        . <br />
        {translate(STRINGS.LETS_CONTINUE_TO_NEXT_SECTION)}
      </p>
    );
    setOpen(true);
  };

  const handleUncompletedSectionFinished = (hasNextSection: boolean) => {
    if (!hasNextSection) {
      return modal.setModalAsPrompt({
        title: translate(STRINGS.SAVE_FOR_LATER),
        message: translate(STRINGS.UNANSWERED_LAST_SECTION_FOR_NOW),
        icon: (
          <SetupWizardVectorWrapper>
            <SetupWizardVector />
          </SetupWizardVectorWrapper>
        ),
        withoutClose: true,
        actions: [
          {
            key: "answer",
            children: translate(STRINGS.ANSWER_INCOMPLETE_QUESTIONS),
            onClick: () => {
              props.onClickGoToFirstUnansweredStage();
              modal.setModalAsClosed();
            },
            variant: "filled",
            color: "primary",
          },
          {
            key: "back",
            children: translate(STRINGS.BACK_TO_ONBOARDING),
            onClick: props.onClickGoBack,
            variant: "outline",
            color: "primary",
          },
        ],
      });
    }

    setTitle(translate(STRINGS.SAVE_FOR_LATER));
    setMessage(
      <p>
        {translate(STRINGS.YOU_DIDNT_ANSWER_ALL_QUESTIONS)}
        <br />
        {translate(STRINGS.YOU_CAN_CONTINUE_TO_NEXT_SECTION)}
      </p>
    );
    return setOpen(true);
  };

  const handleMoveToNextSection = () => {
    const nextSection = getNextSection();

    if (nextSection === undefined) {
      props.onClickGoBack();
      return;
    }

    props.onRequestNavigateReplace({
      type: "OnboardingSectionActivity",
      sections: props.sections,
      section: nextSection,
      caregiverApplicationQuery: props.caregiverApplicationQuery,
      language: props.language,
      pageKey: nextSection.data.id.toString(),
      customDirectionParams: null,
    });

    setOpen(false);
  };

  return {
    isOpen,
    appModal: modal,
    title,
    message,
    handleSectionFinished,
    handleMoveToNextSection,
  };
}

function sortBooleansTrueLast(a: boolean, b: boolean) {
  return a === b ? 0 : a ? 1 : -1;
}

export default useNextStageModal;
